export interface MILightingOptions {
  type?: 'sun' | 'fixed' | 'custom';
  dateTime?: Date;
  timeZone?: string;
  enableSunTracking?: boolean;
  sunTrackingSpeed?: number;
  azimuth?: number;
  elevation?: number;
  ambientIntensity?: number;
  ambientColor?: string;
  directionalIntensity?: number;
  directionalColor?: string;
  shadows?: MIShadowOptions;
  additionalLights?: MILight[];
}

export interface MIShadowOptions {
  enabled: boolean;
  type?: 'hard' | 'soft';
  cascades?: 1 | 2 | 3 | 4;
  resolution?: 512 | 1024 | 2048 | 4096;
  softness?: number;
  bias?: number;
  normalBias?: number;
  maxDistance?: number;
  fadeDistance?: number;
  darkness?: number;
  terrainCastShadows?: boolean;
  terrainReceiveShadows?: boolean;
}

export interface MILight {
  type: 'point' | 'spot' | 'directional';
  color?: string;
  intensity?: number;
  castShadows?: boolean;
  position?: [number, number, number];
  range?: number;
  decay?: number;
  direction?: [number, number, number];
  innerConeAngle?: number;
  outerConeAngle?: number;
  azimuth?: number;
  elevation?: number;
}

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

const DEFAULT_SHADOW_OPTIONS: MIShadowOptions = {
  enabled: false,
  type: 'soft',
  cascades: 3,
  resolution: 2048,
  softness: 0.5,
  bias: 0.0005,
  normalBias: 0.01,
  maxDistance: 5000,
  fadeDistance: 500,
  darkness: 0.5,
  terrainCastShadows: true,
  terrainReceiveShadows: true,
};

let lightIdCounter = 0;
function generateLightId(): string {
  return `light_${++lightIdCounter}_${Date.now()}`;
}

export class SunPosition {
  static calculate(
    date: Date,
    latitude: number,
    longitude: number
  ): { azimuth: number; elevation: number; declination: number; hourAngle: number } {
    const jd = SunPosition._toJulianDate(date);
    const jc = (jd - 2451545.0) / 36525.0;

    const geomMeanLongSun = (280.46646 + jc * (36000.76983 + 0.0003032 * jc)) % 360;
    const geomMeanAnomSun = 357.52911 + jc * (35999.05029 - 0.0001537 * jc);
    const eccentEarthOrbit = 0.016708634 - jc * (0.000042037 + 0.0000001267 * jc);

    const anomRad = geomMeanAnomSun * DEG_TO_RAD;
    const sunEqOfCenter =
      Math.sin(anomRad) * (1.914602 - jc * (0.004817 + 0.000014 * jc)) +
      Math.sin(2 * anomRad) * (0.019993 - 0.000101 * jc) +
      Math.sin(3 * anomRad) * 0.000289;

    const sunTrueLong = geomMeanLongSun + sunEqOfCenter;
    const sunAppLong = sunTrueLong - 0.00569 - 0.00478 * Math.sin((125.04 - 1934.136 * jc) * DEG_TO_RAD);

    const meanObliqEcliptic = 23.0 + (26.0 + (21.448 - jc * (46.815 + jc * (0.00059 - jc * 0.001813))) / 60.0) / 60.0;
    const obliqCorr = meanObliqEcliptic + 0.00256 * Math.cos((125.04 - 1934.136 * jc) * DEG_TO_RAD);

    const declination = Math.asin(Math.sin(obliqCorr * DEG_TO_RAD) * Math.sin(sunAppLong * DEG_TO_RAD)) * RAD_TO_DEG;

    const varY = Math.tan((obliqCorr / 2) * DEG_TO_RAD) ** 2;
    const eqOfTime =
      4 *
      RAD_TO_DEG *
      (varY * Math.sin(2 * geomMeanLongSun * DEG_TO_RAD) -
        2 * eccentEarthOrbit * Math.sin(anomRad) +
        4 * eccentEarthOrbit * varY * Math.sin(anomRad) * Math.cos(2 * geomMeanLongSun * DEG_TO_RAD) -
        0.5 * varY * varY * Math.sin(4 * geomMeanLongSun * DEG_TO_RAD) -
        1.25 * eccentEarthOrbit * eccentEarthOrbit * Math.sin(2 * anomRad));

    const utcHours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
    const solarTimeFix = eqOfTime + 4 * longitude;
    const trueSolarTime = ((utcHours * 60 + solarTimeFix) % 1440 + 1440) % 1440;

    let hourAngle = trueSolarTime / 4 - 180;
    if (hourAngle < -180) hourAngle += 360;

    const latRad = latitude * DEG_TO_RAD;
    const decRad = declination * DEG_TO_RAD;
    const haRad = hourAngle * DEG_TO_RAD;

    const sinElevation =
      Math.sin(latRad) * Math.sin(decRad) + Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
    const elevation = Math.asin(Math.max(-1, Math.min(1, sinElevation))) * RAD_TO_DEG;

    const cosAzimuth =
      (Math.sin(decRad) - Math.sin(latRad) * Math.sin(elevation * DEG_TO_RAD)) /
      (Math.cos(latRad) * Math.cos(elevation * DEG_TO_RAD));

    let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAzimuth))) * RAD_TO_DEG;

    if (hourAngle > 0) {
      azimuth = 360 - azimuth;
    }

    return {
      azimuth,
      elevation,
      declination,
      hourAngle,
    };
  }

  private static _toJulianDate(date: Date): number {
    const y = date.getUTCFullYear();
    const m = date.getUTCMonth() + 1;
    const d = date.getUTCDate();
    const h = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;

    let jy = y;
    let jm = m;
    if (m <= 2) {
      jy -= 1;
      jm += 12;
    }

    const a = Math.floor(jy / 100);
    const b = 2 - a + Math.floor(a / 4);

    return Math.floor(365.25 * (jy + 4716)) + Math.floor(30.6001 * (jm + 1)) + d + h / 24 + b - 1524.5;
  }
}

export class LightingManager {
  private _options: MILightingOptions;
  private _lights: Map<string, MILight>;
  private _shadowOptions: MIShadowOptions;
  private _lightingTime: Date;
  private _sunTrackingInterval: ReturnType<typeof setInterval> | null;
  private _sunTrackingSpeed: number;
  private _latitude: number;
  private _longitude: number;

  constructor(options?: MILightingOptions) {
    this._options = options ? { ...options } : {};
    this._lights = new Map();
    this._shadowOptions = options?.shadows ? { ...DEFAULT_SHADOW_OPTIONS, ...options.shadows } : { ...DEFAULT_SHADOW_OPTIONS };
    this._lightingTime = options?.dateTime ? new Date(options.dateTime.getTime()) : new Date();
    this._sunTrackingInterval = null;
    this._sunTrackingSpeed = options?.sunTrackingSpeed ?? 1;
    this._latitude = 0;
    this._longitude = 0;

    if (options?.additionalLights) {
      for (const light of options.additionalLights) {
        const id = generateLightId();
        this._lights.set(id, { ...light });
      }
    }

    if (options?.enableSunTracking) {
      this.enableSunTracking(this._sunTrackingSpeed);
    }
  }

  setOptions(options: MILightingOptions): void {
    this._options = { ...this._options, ...options };

    if (options.dateTime !== undefined) {
      this._lightingTime = new Date(options.dateTime.getTime());
    }

    if (options.shadows !== undefined) {
      this._shadowOptions = { ...this._shadowOptions, ...options.shadows };
    }

    if (options.additionalLights !== undefined) {
      this._lights.clear();
      for (const light of options.additionalLights) {
        const id = generateLightId();
        this._lights.set(id, { ...light });
      }
    }

    if (options.enableSunTracking !== undefined) {
      if (options.enableSunTracking) {
        this.enableSunTracking(options.sunTrackingSpeed ?? this._sunTrackingSpeed);
      } else {
        this.disableSunTracking();
      }
    }
  }

  getOptions(): MILightingOptions {
    return {
      ...this._options,
      dateTime: new Date(this._lightingTime.getTime()),
      shadows: { ...this._shadowOptions },
      additionalLights: Array.from(this._lights.values()).map((l) => ({ ...l })),
    };
  }

  setLightingTime(date: Date): void {
    this._lightingTime = new Date(date.getTime());
    this._options.dateTime = new Date(date.getTime());
    if ((this as any)._attachedViewer) {
      try { (this as any)._applyToScene(); } catch {}
    }
  }

  getLightingTime(): Date {
    return new Date(this._lightingTime.getTime());
  }

  enableSunTracking(speed?: number): void {
    this.disableSunTracking();
    this._sunTrackingSpeed = speed ?? 1;
    this._options.enableSunTracking = true;
    this._options.sunTrackingSpeed = this._sunTrackingSpeed;

    const intervalMs = 1000;

    this._sunTrackingInterval = setInterval(() => {
      const elapsed = intervalMs * this._sunTrackingSpeed;
      this._lightingTime = new Date(this._lightingTime.getTime() + elapsed);
    }, intervalMs);
  }

  disableSunTracking(): void {
    if (this._sunTrackingInterval !== null) {
      clearInterval(this._sunTrackingInterval);
      this._sunTrackingInterval = null;
    }
    this._options.enableSunTracking = false;
  }

  getSunPosition(): { azimuth: number; elevation: number } {
    const result = SunPosition.calculate(this._lightingTime, this._latitude, this._longitude);
    return { azimuth: result.azimuth, elevation: result.elevation };
  }

  addLight(light: MILight): string {
    const id = generateLightId();
    this._lights.set(id, { ...light });
    return id;
  }

  removeLight(lightId: string): void {
    this._lights.delete(lightId);
  }

  getLights(): MILight[] {
    return Array.from(this._lights.values()).map((l) => ({ ...l }));
  }

  getShadowOptions(): MIShadowOptions {
    return { ...this._shadowOptions };
  }

  // ─── Cesium scene wiring ─────────────────────────────────────────────────
  private _attachedViewer: any = null;
  private _removeTickListener: (() => void) | null = null;

  /**
   * Attach this manager to a Cesium viewer. Configures globe lighting,
   * shadow map, and (when enableSunTracking is on) drives `viewer.clock`
   * forward each tick so the sun position updates in real time.
   */
  attachToScene(viewer: any): void {
    if (!viewer || !viewer.scene) return;
    this.detachFromScene();
    this._attachedViewer = viewer;
    this._applyToScene();
    if (this._options.enableSunTracking) {
      const onTick = () => {
        try {
          const Cesium = (globalThis as any).Cesium;
          if (Cesium && viewer.clock) {
            viewer.clock.currentTime = Cesium.JulianDate.fromDate(this._lightingTime);
          }
        } catch {}
      };
      viewer.scene.preRender.addEventListener(onTick);
      this._removeTickListener = () => {
        try { viewer.scene.preRender.removeEventListener(onTick); } catch {}
      };
    }
  }

  detachFromScene(): void {
    if (this._removeTickListener) { try { this._removeTickListener(); } catch {} this._removeTickListener = null; }
    this._attachedViewer = null;
  }

  private _applyToScene(): void {
    const v = this._attachedViewer;
    if (!v || !v.scene) return;
    try {
      v.scene.globe.enableLighting = true;
      const sm = v.scene.shadowMap;
      if (sm) {
        sm.enabled = !!this._shadowOptions.enabled;
        if (this._shadowOptions.softness !== undefined) sm.softShadows = this._shadowOptions.softness > 0;
        if (this._shadowOptions.darkness !== undefined) sm.darkness = this._shadowOptions.darkness;
        if (this._shadowOptions.maxDistance !== undefined) sm.maximumDistance = this._shadowOptions.maxDistance;
      }
      const Cesium = (globalThis as any).Cesium;
      if (Cesium && v.clock) {
        v.clock.currentTime = Cesium.JulianDate.fromDate(this._lightingTime);
      }
    } catch {}
  }

  setShadowOptions(options: Partial<MIShadowOptions>): void {
    this._shadowOptions = { ...this._shadowOptions, ...options };
    this._options.shadows = { ...this._shadowOptions };
    this._applyToScene();
  }

  destroy(): void {
    this.detachFromScene();
    this.disableSunTracking();
    this._lights.clear();
    this._options = {};
    this._shadowOptions = { ...DEFAULT_SHADOW_OPTIONS };
    this._lightingTime = new Date();
  }
}
